import React, { useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Star } from 'lucide-react';

// Fix default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const priceIcon = (price) =>
  L.divIcon({
    className: 'custom-price-marker',
    html: `<div style="background:#fff;border:2px solid #222;border-radius:20px;padding:4px 10px;font-weight:700;font-size:13px;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.15);">$${Math.round(price)}</div>`,
    iconSize: [0, 0],
    iconAnchor: [30, 15],
  });

const COORDS = {
  'Abu Dhabi, UAE': [24.4539, 54.3773],
  'Atlanta, GA': [33.749, -84.388],
  'Austin, TX': [30.2672, -97.7431],
  'Barcelona, Spain': [41.3874, 2.1686],
  'Boston, MA': [42.3601, -71.0589],
  'Brooklyn, NY': [40.6782, -73.9442],
  'Chicago, IL': [41.8781, -87.6298],
  'Denver, CO': [39.7392, -104.9903],
  'Dubai, UAE': [25.2048, 55.2708],
  'London, UK': [51.5074, -0.1278],
  'Los Angeles, CA': [34.0522, -118.2437],
  'Miami, FL': [25.7617, -80.1918],
  'Nashville, TN': [36.1627, -86.7816],
  'New Orleans, LA': [29.9511, -90.0715],
  'New York, NY': [40.7128, -74.006],
  'Paris, France': [48.8566, 2.3522],
  'Philadelphia, PA': [39.9526, -75.1652],
  'Portland, OR': [45.5152, -122.6784],
  'Rome, Italy': [41.9028, 12.4964],
  'San Diego, CA': [32.7157, -117.1611],
  'San Francisco, CA': [37.7749, -122.4194],
  'Seattle, WA': [47.6062, -122.3321],
};

const ExperienceMap = ({ experiences, splitView = false }) => {
  const mapRef = useRef(null);

  const markers = useMemo(() => {
    return experiences
      .filter((exp) => COORDS[exp.location])
      .map((exp) => {
        const [lat, lng] = COORDS[exp.location];
        const jitter = () => (Math.random() - 0.5) * 0.008;
        return { ...exp, lat: lat + jitter(), lng: lng + jitter() };
      });
  }, [experiences]);

  const bounds = useMemo(() => {
    if (markers.length === 0) return null;
    return L.latLngBounds(markers.map((m) => [m.lat, m.lng]));
  }, [markers]);

  // When the map is ready, invalidate size and fit bounds
  const handleMapReady = useCallback(() => {
    if (mapRef.current && bounds) {
      const map = mapRef.current;
      setTimeout(() => {
        map.invalidateSize();
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
      }, 100);
    }
  }, [bounds]);

  if (markers.length === 0) {
    return (
      <div className="flex items-center justify-center bg-gray-100 rounded-xl border" style={{ height: splitView ? '100%' : '600px' }} data-testid="map-empty">
        <p className="text-gray-500">No locations to display on map</p>
      </div>
    );
  }

  return (
    <div
      className={splitView ? '' : 'rounded-xl overflow-hidden border shadow-sm'}
      data-testid="experience-map"
      style={{ height: splitView ? '100%' : '600px', width: '100%' }}
    >
      <MapContainer
        ref={mapRef}
        center={[38, -30]}
        zoom={3}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        whenReady={handleMapReady}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {markers.map((exp) => (
          <Marker key={exp.id} position={[exp.lat, exp.lng]} icon={priceIcon(exp.price)}>
            <Popup maxWidth={260} minWidth={220}>
              <Link to={`/experience/${exp.id}`} className="block no-underline text-inherit">
                <div style={{ margin: '-14px -20px -14px -20px' }}>
                  {exp.images?.[0] && (
                    <img
                      src={exp.images[0]}
                      alt={exp.title}
                      style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '4px 4px 0 0' }}
                    />
                  )}
                  <div style={{ padding: '10px 14px' }}>
                    <p style={{ fontSize: '11px', color: '#666', margin: 0 }}>{exp.location}</p>
                    <p style={{ fontSize: '14px', fontWeight: 600, margin: '2px 0 4px', lineHeight: 1.3 }}>{exp.title}</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 700, fontSize: '14px' }}>
                        ${exp.price}
                        <span style={{ fontWeight: 400, fontSize: '12px', color: '#666' }}>/person</span>
                      </span>
                      {exp.average_rating > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: '12px', color: '#666' }}>
                          <Star style={{ width: 12, height: 12, fill: '#f59e0b', color: '#f59e0b' }} />
                          {exp.average_rating?.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default ExperienceMap;
